/**
 * EmailsSection Component
 *
 * Displays and manages emails for a contact or company.
 * Supports add/delete operations with inline forms.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contactsApi, companiesApi } from '@/services/contactsApi';
import type { ContactEmail, CompanyEmail, ContactEmailInput, CompanyEmailInput } from '../types';

interface EmailsSectionProps {
  emails: (ContactEmail | CompanyEmail)[];
  entityId: string;
  entityType: 'contact' | 'company';
  onUpdate: () => void;
  onError: (error: string) => void;
}

export default function EmailsSection({
  emails,
  entityId,
  entityType,
  onUpdate,
  onError,
}: EmailsSectionProps) {
  const { t } = useTranslation('people');
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState<ContactEmailInput | CompanyEmailInput>({
    email: '',
    email_type: entityType === 'contact' ? 'work' : 'general',
    is_primary: false,
  });

  const handleAdd = async () => {
    if (!newEmail.email.trim()) return;

    try {
      if (entityType === 'contact') {
        await contactsApi.addEmail(entityId, newEmail as ContactEmailInput);
      } else {
        await companiesApi.addEmail(entityId, newEmail as CompanyEmailInput);
      }
      onUpdate();
      setShowAdd(false);
      setNewEmail({
        email: '',
        email_type: entityType === 'contact' ? 'work' : 'general',
        is_primary: false,
      });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to add email');
    }
  };

  const handleDelete = async (emailId: string, emailAddress: string) => {
    if (!confirm(`Delete email ${emailAddress}?`)) return;

    try {
      if (entityType === 'contact') {
        await contactsApi.deleteEmail(entityId, emailId);
      } else {
        await companiesApi.deleteEmail(entityId, emailId);
      }
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to delete email');
    }
  };

  const emailTypes = entityType === 'contact'
    ? ['work', 'personal', 'other']
    : ['general', 'sales', 'support', 'billing', 'hr'];

  return (
    <div className="mb-6">
      <label className="block text-xs font-medium text-gray-500 uppercase mb-2">{t('sections.emails.label')}</label>
      <div className="space-y-2">
        {emails && emails.length > 0 ? (
          emails.map((email) => (
            <div key={email.id} className="flex items-center gap-2 text-sm group">
              <button
                onClick={() => handleDelete(email.id, email.email)}
                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                title={t('sections.emails.delete_email')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <a href={`mailto:${email.email}`} className="text-blue-600 hover:underline">
                {email.email}
              </a>
              <span className="text-xs text-gray-400">({email.email_type})</span>
              {email.is_primary && (
                <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded">primary</span>
              )}
            </div>
          ))
        ) : (
          <span className="text-gray-400 text-sm">{t('sections.emails.no_emails')}</span>
        )}

        {/* Add Email Form */}
        {showAdd ? (
          <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-2">
            <input
              type="email"
              value={newEmail.email}
              onChange={(e) => setNewEmail({ ...newEmail, email: e.target.value })}
              placeholder="email@example.com"
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newEmail.email.trim()) {
                  e.preventDefault();
                  handleAdd();
                }
                if (e.key === 'Escape') {
                  setShowAdd(false);
                  setNewEmail({ email: '', email_type: entityType === 'contact' ? 'work' : 'general', is_primary: false });
                }
              }}
            />
            <div className="flex gap-2">
              <select
                value={newEmail.email_type}
                onChange={(e) => setNewEmail({ ...newEmail, email_type: e.target.value })}
                className="px-2 py-1 text-sm border border-gray-300 rounded"
              >
                {emailTypes.map((type) => (
                  <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                ))}
              </select>
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={newEmail.is_primary}
                  onChange={(e) => setNewEmail({ ...newEmail, is_primary: e.target.checked })}
                />
                {t('sections.emails.primary')}
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                disabled={!newEmail.email.trim()}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {t('sections.emails.add')}
              </button>
              <button
                onClick={() => {
                  setShowAdd(false);
                  setNewEmail({ email: '', email_type: entityType === 'contact' ? 'work' : 'general', is_primary: false });
                }}
                className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded"
              >
                {t('sections.emails.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAdd(true)}
            className="mt-1 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('sections.emails.add_email')}
          </button>
        )}
      </div>
    </div>
  );
}
